import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Trophy, X, TrendingUp, Flame, Footprints, Dumbbell, Sparkles } from 'lucide-react';

const STORAGE_KEY_DISMISSED = 'mk_resumen_dismissed';

export default function ResumenSemanal() {
  const [data, setData] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/community/resumen-semanal`);
        if (cancelled) return;
        if (res.data && res.data.id) {
          // Si ya fue marcado como leido localmente Y backend, no mostrar
          const dismissedId = localStorage.getItem(STORAGE_KEY_DISMISSED);
          if (dismissedId === res.data.id || res.data.leido) return;
          setData(res.data);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = async () => {
    setClosed(true);
    try {
      if (data?.id) localStorage.setItem(STORAGE_KEY_DISMISSED, data.id);
      await axios.post(`${API}/community/resumen-semanal/marcar-leido`);
    } catch { /* ignore */ }
  };

  if (!data || closed) return null;

  const { trainings = 0, fuerza = 0, checkins = 0, km = 0, records = 0, total_actividad = 0, mejor_que_pct = 0 } = data;

  let pctMsg = null;
  if (mejor_que_pct >= 80) pctMsg = `🥇 Estás en el top del Klan — mejor que el ${mejor_que_pct}%`;
  else if (mejor_que_pct >= 60) pctMsg = `🦁 Por encima de la media — mejor que el ${mejor_que_pct}%`;
  else if (mejor_que_pct >= 30) pctMsg = `Sigue así — mejor que el ${mejor_que_pct}% del Klan`;
  else if (total_actividad > 0) pctMsg = `Has empezado — esta semana suma más para subir 💪`;

  return (
    <div
      data-testid="resumen-semanal-card"
      className="relative rounded-xl border border-gold/40 bg-gradient-to-br from-amber-900/30 via-gray-900/60 to-gray-900/30 p-4 sm:p-5 mb-4 overflow-hidden"
    >
      {/* Decoración fondo */}
      <div className="absolute -top-8 -right-8 text-gold/10 pointer-events-none">
        <Sparkles className="w-32 h-32" />
      </div>
      
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-gray-800/60 text-gray-400 hover:text-white"
        title="Cerrar"
        data-testid="btn-cerrar-resumen"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 relative">
        <div className="bg-gold/20 rounded-full p-2 flex-shrink-0">
          <Trophy className="w-5 h-5 text-gold" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="text-[10px] uppercase tracking-widest text-gold/80 font-bold mb-0.5">
            Tu semana pasada en MK
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-white">
            {total_actividad === 0 ? '¡La semana pasada descansaste!' : `Tus actividades de la semana pasada: ${total_actividad}`}
          </h3>
          {pctMsg && (
            <p className="text-sm text-gold/90 mt-1">{pctMsg}</p>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 relative">
        <Stat icon={<TrendingUp className="w-4 h-4" />} value={trainings} label="Entrenos" color="text-blue-400" />
        <Stat icon={<Dumbbell className="w-4 h-4" />} value={fuerza} label="Fuerza" color="text-orange-400" />
        <Stat icon={<Footprints className="w-4 h-4" />} value={km ? `${km}` : 0} label="km" color="text-emerald-400" />
        <Stat icon={<Flame className="w-4 h-4" />} value={records} label={records === 1 ? 'Récord 🏆' : 'Récords 🏆'} color="text-yellow-300" />
      </div>

      <p className="text-[11px] text-gray-500 mt-3 italic relative">
        Semana pasada: del {data.semana_inicio?.split('-').reverse().slice(0, 2).join('/')} al {data.semana_fin?.split('-').reverse().slice(0, 2).join('/')}
      </p>
    </div>
  );
}

function Stat({ icon, value, label, color }) {
  return (
    <div className="bg-gray-900/60 rounded-lg px-3 py-2 border border-gray-800">
      <div className={`flex items-center gap-1 ${color}`}>
        {icon}
        <span className="text-xl font-bold tabular-nums">{value}</span>
      </div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
